// src/hooks/useUrlState.js
// ────────────────────────────────────────────────────────────
// Reusable hook that reads/writes individual URL search params.
//
// Usage:
//   const [range, setRange] = useUrlState('range', '30days');
//
// The URL becomes the source of truth, which means:
//   - State survives page navigation (browser back/fwd)
//   - URLs are shareable / bookmarkable
//   - Filters reset automatically on fresh navigation
// ────────────────────────────────────────────────────────────
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Synchronise a single key in the URL search parameters with
 * React component state.
 *
 * @param {string}  key           – The query-parameter name (e.g. "range")
 * @param {string}  defaultValue  – Value used when the param is absent
 * @returns {[string, (v: string) => void]}
 */
export function useUrlState(key, defaultValue = '') {
  const [searchParams, setSearchParams] = useSearchParams();

  // Current value — falls back to defaultValue when absent
  const value = searchParams.get(key) ?? defaultValue;

  // Setter — merges with existing params (does not clobber others)
  const setValue = useCallback(
    (newValue) => {
      setSearchParams(
        (prev) => {
          // Remove the param when resetting to the default
          if (newValue === defaultValue || newValue === '' || newValue == null) {
            prev.delete(key);
          } else {
            prev.set(key, newValue);
          }
          return prev;
        },
        { replace: true }, // replace history entry so back-button isn't polluted
      );
    },
    [key, defaultValue, setSearchParams],
  );

  return [value, setValue];
}
